import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, addDoc, serverTimestamp } from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import { isCourierCod, codFeeFor } from './dueUtil';
import { printSalesInvoice } from './printInvoice';

const Accounts = () => {
  const [sales, setSales] = useState([]);
  const [deliveryExpenses, setDeliveryExpenses] = useState([]);
  const [codFeeExpenses, setCodFeeExpenses] = useState([]);
  const [manualTransactions, setManualTransactions] = useState([]);

  // ✅ NEW: কোন ইনভয়েসটা "View" করা হচ্ছে সেটা রাখার জন্য state
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  
  const [dateFilter, setDateFilter] = useState({ from: firstDay, to: lastDay });

  const [formData, setFormData] = useState({
    type: 'income',
    category: 'Saree Sale',
    description: '',
    amount: '',
    method: 'Cash',
    date: now.toISOString().split('T')[0]
  });

  useEffect(() => {
    // ✅ FIX: orderBy("createdAt") সরিয়ে ফেলা হয়েছে। createdAt একটি serverTimestamp() ফিল্ড —
    // Firestore-এ orderBy(serverTimestampField) কোয়েরিতে নতুন যোগ করা ডকুমেন্ট ততক্ষণ দেখায় না
    // যতক্ষণ না সার্ভার থেকে আসল টাইমস্ট্যাম্প কনফার্ম হয়। তাই এখন কোনো orderBy ছাড়াই
    // সব ডেটা নিয়ে JavaScript-এ (client-side) সর্ট করা হচ্ছে — এতে নতুন সেল সাথে সাথেই দেখাবে।
    const q = query(collection(db, "sales"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const salesData = snapshot.docs.map(doc => {
        const data = doc.data();
        const totalPurchaseCost = data.items?.reduce((sum, item) => sum + (Number(item.purchasePrice || 0) * item.qty), 0) || 0;
        // একটা ইনভয়েসে একাধিক Business Type (যেমন শাড়ি + জুয়েলারি) একসাথে থাকতে পারে,
        // তাই লেবেলে সব ধরনের নাম দেখানো হয়। isSale ফ্ল্যাগ দিয়ে ম্যানুয়াল এন্ট্রি থেকে
        // আলাদা করা হয় (ক্যাটাগরির লেবেল বদলে গেলেও এই চেক ভাঙবে না)।
        const saleTypes = Array.from(new Set((data.items || []).map(it => getBusinessType(it))));
        return {
          id: doc.id,
          ...data,
          type: 'income',
          isSale: true,
          category: saleTypes.length > 0 ? `${saleTypes.join(' + ')} Sale` : 'Saree Sale',
          description: `Invoice: ${data.invoiceId}`,
          date: data.createdAt?.toDate ? data.createdAt.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          amount: Number(data.totalBill) || 0,
          due: Number(data.dueAmount || 0),
          customerName: data.name || 'Unknown',
          customerPhone: [data.phone, data.phone2].filter(Boolean).join(', ') || '',
          purchaseCost: totalPurchaseCost,
          paid: Number(data.paid || 0),
          method: data.paidMethod || 'Cash' // ✅ NEW: Sales পেজে বেছে দেওয়া Cash/Bank/bKash
        };
      });
      setSales(salesData);

      // ---- দোকান নিজে ডেলিভারি চার্জ দিলে সেটা আগে শুধু Net Profit-এর হিসাবে চুপচাপ বাদ
      // যেত, লেজারে কোনো লাইন হিসেবে দেখা যেত না। এখন প্রতিটার জন্য একটা আলাদা expense
      // এন্ট্রি বসানো হচ্ছে, যাতে Ledger Book-এ আসল খরচ হিসেবে দেখা যায় ও ট্র্যাক করা যায়। ----
      const deliveryExpenseData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(data => data.deliveryPaidBy === 'shop' && Number(data.deliveryCharge || 0) > 0)
        .map(data => ({
          id: `${data.id}-delivery`,
          type: 'expense',
          category: 'Delivery Charge',
          description: `Invoice: ${data.invoiceId} (shop-paid delivery)`,
          date: data.createdAt?.toDate ? data.createdAt.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          amount: Number(data.deliveryCharge || 0),
          method: 'Cash',
          isDeliveryExpense: true
        }));
      setDeliveryExpenses(deliveryExpenseData);

      // ---- Pathao COD টাকার ১% "COD Fee" কেটে নেয় -- ডেলিভারি চার্জের বাইরে আলাদা কাটা, সবসময় দোকানের
      // খরচ (কে ডেলিভারি চার্জ দিচ্ছে তার সাথে সম্পর্ক নেই)। ডেলিভারি খরচের মতোই আলাদা expense
      // লাইন হিসেবে বসে, তাই Net Profit-এ আপনা-আপনি কমে যায়। এটা Cash থেকে যায় না -- Pathao
      // দোকানকে COD-র টাকা পাঠানোর সময় নিজেই কেটে রাখে (পেমেন্ট মেথড ব্রেকডাউনে তাই ধরা হয় না)। ----
      const codFeeExpenseData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(data => codFeeFor(data) > 0)
        .map(data => ({
          id: `${data.id}-codfee`,
          type: 'expense',
          category: 'Courier COD Fee',
          description: `Invoice: ${data.invoiceId} (Pathao 1% COD fee)`,
          date: data.createdAt?.toDate ? data.createdAt.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          amount: codFeeFor(data),
          method: 'Pathao',
          isCodFeeExpense: true
        }));
      setCodFeeExpenses(codFeeExpenseData);
    });

    // ---- ম্যানুয়াল আয়/খরচের এন্ট্রি Firestore-এ সেভ থাকে (আগে শুধু ব্রাউজারের মেমোরিতে
    // থাকত, তাই পেজ রিফ্রেশ করলেই হারিয়ে যেত) ----
    const unsubTx = onSnapshot(query(collection(db, "transactions")), (snapshot) => {
      setManualTransactions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubscribe(); unsubTx(); };
  }, []);

  const allTransactions = useMemo(() => {
    return [...sales, ...deliveryExpenses, ...codFeeExpenses, ...manualTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [sales, deliveryExpenses, codFeeExpenses, manualTransactions]);

  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(t => t.date >= dateFilter.from && t.date <= dateFilter.to);
  }, [allTransactions, dateFilter]);

  // ---- একটা ট্রানজেকশনকে "সেল" হিসেবে ধরা হয় যদি সেটা Sales পেজ থেকে অটোম্যাটিক এসে থাকে (isSale),
  // অথবা ম্যানুয়ালি "___ Sale" ক্যাটাগরিতে যোগ করা হয়ে থাকে (আগের মতোই আচরণ বজায় রাখতে) ----
  const isSaleTx = (t) => t.isSale || (t.category || '').endsWith(' Sale');
  const totalSales = filteredTransactions.filter(t => t.type === 'income' && isSaleTx(t)).reduce((sum, t) => sum + t.amount, 0);
  const totalPurchaseCost = filteredTransactions.filter(t => t.type === 'income' && isSaleTx(t)).reduce((sum, t) => sum + (t.purchaseCost || 0), 0);
  const totalOtherIncome = filteredTransactions.filter(t => t.type === 'income' && !isSaleTx(t)).reduce((sum, t) => sum + t.amount, 0);
  // ---- কাস্টমার-পেইড ডেলিভারি চার্জ ইনভয়েসের totalBill-এর ভেতরেই থাকে (totalSales-এ ধরা
  // পড়ে গেছে), কিন্তু সেটা দোকানের আয় না — তাই এখান থেকে বাদ যায়। শপ-পেইড ডেলিভারি এখন
  // আলাদা expense লাইন হিসেবে totalExpense-এ ধরা পড়ে, তাই এখানে আবার বাদ দিলে ডাবল হয়ে যাবে। ----
  const totalDeliveryCharge = filteredTransactions.filter(t => t.isSale && t.deliveryPaidBy !== 'shop').reduce((sum, t) => sum + Number(t.deliveryCharge || 0), 0);

  // ---- প্রতিটা Business Type-এর সেল আলাদা করে (আইটেম লেভেলে ভাগ করে) ----
  const salesByType = useMemo(() => {
    const totals = {};
    BUSINESS_TYPES.forEach(t => { totals[t] = 0; });
    filteredTransactions.filter(t => t.isSale).forEach(sale => {
      (sale.items || []).forEach(it => {
        const bType = getBusinessType(it);
        const amt = Number(it.finalItemTotal ?? (it.price * it.qty)) || 0;
        totals[bType] = (totals[bType] || 0) + amt;
      });
    });
    return totals;
  }, [filteredTransactions]);
  // ---- Cash/Bank/bKash/Nagad -- কোন মেথডে এখন পর্যন্ত কত টাকা এসেছে/গেছে, সেই হিসাব ----
  // Sales থেকে শুধু paid/advance অংশটুকু ধরা হয় (Due/COD এখনো হাতে আসেনি, তাই বাদ),
  // Sales পেজে বেছে দেওয়া method অনুযায়ী। ম্যানুয়াল income/expense নিজের method অনুযায়ী
  // যোগ/বাদ হয়। শপ-পেইড ডেলিভারি খরচ সবসময় Cash থেকে গেছে ধরা হয়েছে (আগের মতোই)।
  const paymentMethodTotals = useMemo(() => {
    const totals = { Cash: 0, Bank: 0, bKash: 0, Nagad: 0 };
    filteredTransactions.filter(t => t.isSale).forEach(sale => {
      const m = totals.hasOwnProperty(sale.method) ? sale.method : 'Cash';
      totals[m] += Number(sale.paid || 0);
    });
    filteredTransactions.filter(t => !t.isSale && !t.isDeliveryExpense && !t.isCodFeeExpense).forEach(tx => {
      const m = totals.hasOwnProperty(tx.method) ? tx.method : 'Cash';
      totals[m] += tx.type === 'expense' ? -Number(tx.amount || 0) : Number(tx.amount || 0);
    });
    filteredTransactions.filter(t => t.isDeliveryExpense).forEach(tx => {
      totals.Cash -= Number(tx.amount || 0);
    });
    return totals;
  }, [filteredTransactions]);

  const totalExpense = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
  const totalShopDeliveryExpense = filteredTransactions.filter(t => t.isDeliveryExpense).reduce((sum, t) => sum + t.amount, 0);
  const totalCodFeeExpense = filteredTransactions.filter(t => t.isCodFeeExpense).reduce((sum, t) => sum + t.amount, 0);
  const netProfit = (totalSales - totalDeliveryCharge - totalPurchaseCost) + totalOtherIncome - totalExpense;
  
  // ---- কাস্টমারের আসল বকেয়া (due) আর কুরিয়ারের কাছে আটকে থাকা COD টাকা এক জিনিস না --
  // dueUtil.js/Due.js/Contacts.js সব জায়গায় এই দুইটা আলাদা রাখা হয় (একসাথে মেশালে ভালো
  // কাস্টমারকেও বাকিদার মনে হয়), কিন্তু এখানে আগে সেই বাদ দেওয়াটা করা হতো না -- ফলে
  // Receivables কার্ডে COD টাকাও কাস্টমার-ডিউ হিসেবে যোগ হয়ে যেত। ----
  const receivablesList = sales.filter(s => s.due > 0 && !isCourierCod(s));
  const totalReceivables = receivablesList.reduce((sum, s) => sum + s.due, 0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount' && value < 0) return; 
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || Number(formData.amount) <= 0) return alert("Please enter a valid amount!");
    try {
      await addDoc(collection(db, "transactions"), {
        ...formData,
        amount: parseFloat(formData.amount),
        addedBy: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff',
        createdAt: serverTimestamp()
      });
      setFormData({ ...formData, description: '', amount: '' });
    } catch (err) {
      alert("Could not save the transaction: " + err.message);
    }
  };

  return (
    <div style={{ padding: '24px', fontFamily: 'sans-serif', color: '#1e293b', background: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Header & Master Filter Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>Accounts & Ledger</h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>Real-time business tracking & profit analysis</p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#fff', padding: '10px 15px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <label style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8' }}>FROM</label>
            <input type="date" value={dateFilter.from} onChange={e => setDateFilter({...dateFilter, from: e.target.value})} style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <label style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8' }}>TO</label>
            <input type="date" value={dateFilter.to} onChange={e => setDateFilter({...dateFilter, to: e.target.value})} style={{ padding: '5px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }} />
          </div>
          <button style={{ padding: '10px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '13px', alignSelf: 'flex-end' }}>📄 Export Report</button>
        </div>
      </div>

      {/* Summary Cards - Original Icons & Styles Restored */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '5px solid #10b981' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>TOTAL INCOME</span>
            <span>📈</span>
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>৳ {totalSales.toLocaleString()}</h2>
        </div>

        <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '5px solid #ef4444' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>TOTAL EXPENSE</span>
            <span>📉</span>
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>৳ {totalExpense.toLocaleString()}</h2>
        </div>

        <div className="hover-lift" style={{ background: netProfit >= 0 ? '#e6f4ea' : '#fce8e6', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: `5px solid ${netProfit >= 0 ? '#10b981' : '#ef4444'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>NET PROFIT</span>
            <span>💰</span>
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0, color: netProfit >= 0 ? '#137333' : '#c5221f' }}>৳ {netProfit.toLocaleString()}</h2>
          {totalDeliveryCharge > 0 && (
            <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#64748b' }}>
              🚚 Customer-paid delivery ৳ {totalDeliveryCharge.toLocaleString()} excluded (courier's money, not profit)
            </p>
          )}
          {totalShopDeliveryExpense > 0 && (
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b' }}>
              🚚 Shop-paid delivery ৳ {totalShopDeliveryExpense.toLocaleString()} counted as expense below
            </p>
          )}
          {totalCodFeeExpense > 0 && (
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b' }}>
              📦 Pathao COD fee (1%) ৳ {totalCodFeeExpense.toLocaleString(undefined, { maximumFractionDigits: 2 })} counted as expense below
            </p>
          )}
        </div>

        <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '5px solid #f59e0b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>RECEIVABLES</span>
            <span>👤</span>
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>৳ {totalReceivables.toLocaleString()}</h2>
          <div style={{marginTop: '10px', fontSize: '11px', color: '#64748b', maxHeight: '55px', overflowY: 'auto'}}>
            {receivablesList.map((r, i) => (
              <div key={i} className="hover-row" style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #f1f5f9', padding:'3px 0', gap: '6px'}}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.customerName}{r.customerPhone ? ` (${r.customerPhone})` : ''}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <b style={{color:'#f59e0b'}}>৳ {r.due}</b>
                  {/* ✅ NEW: View বাটন - ক্লিক করলে এই receivable-টা যে ইনভয়েস থেকে এসেছে সেটা মডালে দেখাবে */}
                  <button
                    onClick={() => setSelectedInvoice(r)}
                    style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      background: '#4f46e5',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Business Type ভিত্তিক সেল ব্রেকডাউন */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '14px', color: '#0f172a' }}>Sales by Business Type</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {BUSINESS_TYPES.map(t => (
            <div className="hover-lift" key={t} style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '10px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>{t}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>৳ {(salesByType[t] || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ NEW: Cash/Bank/bKash/Nagad -- এই টাকাটা এখন কোথায় আছে সেটা এক নজরে */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Cash / Bank / bKash Balance</h3>
        <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '14px' }}>Selected date range only. Sales পেজে Paid/Advance-এ যে মেথড বেছে দেওয়া হয়েছে তা অনুযায়ী -- এখনো হাতে না আসা Due/COD এখানে নেই।</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          {['Cash', 'Bank', 'bKash', 'Nagad'].map(m => (
            <div className="hover-lift" key={m} style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '10px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>{m}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: paymentMethodTotals[m] < 0 ? '#ef4444' : '#0f172a' }}>৳ {paymentMethodTotals[m].toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', alignItems: 'flex-start' }}>
        
        {/* Transaction Form - Original Styles & All Categories */}
        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Add Transaction</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Transaction Type</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setFormData({ ...formData, type: 'income', category: 'Saree Sale' })} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid', background: formData.type === 'income' ? '#e6f4ea' : '#fff', color: formData.type === 'income' ? '#137333' : '#475569', borderColor: formData.type === 'income' ? '#34a853' : '#cbd5e1', cursor: 'pointer', fontWeight: '600' }}>Income (+)</button>
                <button type="button" onClick={() => setFormData({ ...formData, type: 'expense', category: 'Shop Rent' })} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid', background: formData.type === 'expense' ? '#fce8e6' : '#fff', color: formData.type === 'expense' ? '#c5221f' : '#475569', borderColor: formData.type === 'expense' ? '#d93025' : '#cbd5e1', cursor: 'pointer', fontWeight: '600' }}>Expense (-)</button>
              </div>
            </div>
            <input type="date" name="date" value={formData.date} onChange={handleInputChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} required />
            <select name="category" value={formData.category} onChange={handleInputChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
              {formData.type === 'income' ? (
                <>
                  {BUSINESS_TYPES.map(t => <option key={t} value={`${t} Sale`}>{t} Sale</option>)}
                  <option value="Due Collection">Due Collection</option><option value="Advance Booking">Advance Booking</option><option value="Other Income">Other Income</option>
                </>
              ) : (
                <><option value="Delivery Charge">Delivery Charge</option><option value="Weaver Bill">Weaver Bill</option><option value="Shop Rent">Shop Rent</option><option value="Salary">Salary</option><option value="Transport">Transport</option><option value="Electricity">Electricity</option><option value="Packaging">Packaging</option><option value="Refreshment">Refreshment</option><option value="Other Expense">Other Expense</option></>
              )}
            </select>
            <input type="number" name="amount" min="0" value={formData.amount} onChange={handleInputChange} placeholder="Amount (৳)" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} required />
            <select name="method" value={formData.method} onChange={handleInputChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
              <option value="Cash">Cash</option><option value="bKash">bKash</option><option value="Nagad">Nagad</option><option value="Bank">Bank</option>
            </select>
            <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Description/Note..." style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', minHeight: '60px' }}></textarea>
            <button type="submit" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#4f46e5', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>Save Transaction</button>
          </form>
        </div>

        {/* Ledger Table - Original Pill Design & Sub Total Restored */}
        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Ledger Book (Recent Transactions)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f1f5f9', color: '#64748b' }}>
                  <th style={{ padding: '12px 8px' }}>Date</th>
                  <th style={{ padding: '12px 8px' }}>Category & Description</th>
                  <th style={{ padding: '12px 8px' }}>Method</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '14px 8px', color: '#475569', fontWeight: '500' }}>{tx.date}</td>
                    <td style={{ padding: '14px 8px' }}>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{tx.category}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{tx.description}</div>
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      <span style={{ fontSize: '12px', padding: '4px 8px', background: '#f1f5f9', borderRadius: '12px', fontWeight: '600', color: '#475569' }}>{tx.method}</span>
                    </td>
                    <td style={{ padding: '14px 8px', textAlign: 'right', fontWeight: '700', color: tx.type === 'income' ? '#10b981' : '#ef4444' }}>
                      {tx.type === 'income' ? '+' : '-'} ৳ {tx.amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', fontWeight: 'bold', borderTop: '2px solid #e2e8f0' }}>
                  <td colSpan="3" style={{ padding: '14px 8px', textAlign: 'right', color: '#475569' }}>SUB TOTAL:</td>
                  <td style={{ padding: '14px 8px', textAlign: 'right', fontSize: '16px', color: (totalSales + totalOtherIncome - totalExpense) >= 0 ? '#10b981' : '#ef4444' }}>
                    ৳ {(totalSales + totalOtherIncome - totalExpense).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* ✅ NEW: Invoice View Modal — Receivables লিস্টে "View" চাপলে এটা খুলবে */}
      {selectedInvoice && (
        <div
          onClick={() => setSelectedInvoice(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '14px',
              width: '100%',
              maxWidth: '480px',
              maxHeight: '85vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Invoice: {selectedInvoice.invoiceId}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{selectedInvoice.date}</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', marginBottom: '16px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Customer</span>
                <span style={{ fontWeight: '600' }}>{selectedInvoice.customerName}</span>
              </div>
              {selectedInvoice.customerPhone && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Phone</span>
                  <span style={{ fontWeight: '600' }}>{selectedInvoice.customerPhone}</span>
                </div>
              )}
            </div>

            {Array.isArray(selectedInvoice.items) && selectedInvoice.items.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Items</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#94a3b8' }}>
                      <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: '600' }}>Item</th>
                      <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: '600' }}>Qty</th>
                      <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: '600' }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 4px' }}>{item.name || item.productName || 'Item'}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}>{item.qty}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'right' }}>৳ {Number(item.price || item.salePrice || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderTop: '2px solid #f1f5f9', paddingTop: '12px', fontSize: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Total Bill</span>
                <span style={{ fontWeight: '700' }}>৳ {Number(selectedInvoice.amount || 0).toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ color: '#64748b' }}>Paid</span>
                <span style={{ fontWeight: '700', color: '#10b981' }}>
                  ৳ {(Number(selectedInvoice.amount || 0) - Number(selectedInvoice.due || 0)).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Due</span>
                <span style={{ fontWeight: '700', color: '#f59e0b' }}>৳ {Number(selectedInvoice.due || 0).toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={() => printSalesInvoice(selectedInvoice)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Invoice 🖨️
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;